import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent, ToastService } from '@isophi/core-ui';
import { TranslocoService } from '@jsverse/transloco';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, catchError, filter, finalize, map, of, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs';

import { RouterLinkFactory } from '../../core/router/router-link.factory';
import { ChildService } from '../../core/services/child.service';
import { NgbParserService } from '../../core/services/ngb-parser.service';
import { TransitionReportService } from '../../core/services/transition-report.service';

interface ChooseSection {
  title: string;
  controls: {
    label: string;
    formControlName: string;
    tooltip?: string;
  }[];
}

interface SupportSection {
  key: string;
  hasLevel: boolean;
}

type CompetenceLevel = 'low' | 'reduced' | 'adequate' | 'very_good';

@Component({
    selector: 'app-transition-report',
    templateUrl: './transition-report.component.html',
    styleUrls: ['./transition-report.component.scss'],
    standalone: false
})
export class TransitionReportComponent extends BaseComponent implements OnInit {
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly transitionReportService = inject(TransitionReportService);

  private readonly childService = inject(ChildService);

  private readonly formBuilder = inject(FormBuilder);

  private readonly translocoService = inject(TranslocoService);

  private readonly ngbParserService = inject(NgbParserService);

  private readonly spinner = inject(NgxSpinnerService);

  private readonly toastService = inject(ToastService);

  private readonly links = inject(RouterLinkFactory);

  private readonly router = inject(Router);

  private readonly changeDetectionRef = inject(ChangeDetectorRef);

  private readonly isExternalRedirect$ = this.activatedRoute.queryParams.pipe(
    map((params) => !!params['external-redirect']),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  private readonly childUuid$ = this.activatedRoute.params.pipe(
    filter((params) => !!params['childUuid']),
    map((params) => params['childUuid'] as string),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  protected readonly child$ = this.childUuid$.pipe(
    switchMap((childUuid) => this.childService.getChild(childUuid)),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  protected readonly transitionReportForm = this.formBuilder.group({
    first_name: new FormControl<string>(''),
    last_name: new FormControl<string>(''),
    birth_date: new FormControl<NgbDateStruct>(null, [Validators.required]),
    child_gender: new FormControl<string>(''),
    street_and_house_number: new FormControl<string>('', [Validators.required]),
    city: new FormControl<string>('', [Validators.required]),
    district: new FormControl<string>(''),
    postal_code: new FormControl<string>('', [Validators.required]),
    country: new FormControl<string>(''),
    interests: new FormControl<string>(''),
    comp_learning_describes_success: new FormControl<boolean>(false),
    comp_learning_seeks_help: new FormControl<boolean>(false),
    comp_learning_persists: new FormControl<boolean>(false),
    comp_communication_expresses_clearly: new FormControl<boolean>(false),
    comp_communication_listens: new FormControl<boolean>(false),
    comp_communication_uses_language: new FormControl<boolean>(false),
    comp_social_knows_strengths: new FormControl<boolean>(false),
    comp_social_cooperates: new FormControl<boolean>(false),
    comp_social_resolves_conflicts: new FormControl<boolean>(false),
    comp_citizenship_reflects: new FormControl<boolean>(false),
    comp_citizenship_creates_rules: new FormControl<boolean>(false),
    comp_citizenship_empathy: new FormControl<boolean>(false),
    comp_work_plans: new FormControl<boolean>(false),
    comp_work_completes: new FormControl<boolean>(false),
    comp_work_follows_rules: new FormControl<boolean>(false),
    comp_problem_solving_proposes: new FormControl<boolean>(false),
    comp_problem_solving_describes: new FormControl<boolean>(false),
    comp_problem_solving_seeks_alternatives: new FormControl<boolean>(false),
    comp_cultural_participates: new FormControl<boolean>(false),
    comp_cultural_understands_values: new FormControl<boolean>(false),
    comp_cultural_respects_diversity: new FormControl<boolean>(false),
    comp_digital_uses_tools: new FormControl<boolean>(false),
    comp_digital_understands_purpose: new FormControl<boolean>(false),
    comp_digital_follows_rules: new FormControl<boolean>(false),
    additional_strengths: new FormControl<string>(''),
    method_text: new FormControl<string>(''),
    method_speaking_listening: new FormControl<boolean>(false),
    method_demonstration: new FormControl<boolean>(false),
    method_manipulation: new FormControl<boolean>(false),
    method_role_play: new FormControl<boolean>(false),
    method_learning_by_rules: new FormControl<boolean>(false),
    method_creative_learning: new FormControl<boolean>(false),
    method_reflection: new FormControl<boolean>(false),
    method_group_learning: new FormControl<boolean>(false),
    method_digital_support: new FormControl<boolean>(false),
    org_form_collective: new FormControl<boolean>(false),
    org_form_group: new FormControl<boolean>(false),
    org_form_activity_centers: new FormControl<boolean>(false),
    org_form_individual: new FormControl<boolean>(false),
    org_form_free_play: new FormControl<boolean>(false),
    org_form_outdoor: new FormControl<boolean>(false),
    org_form_circle_time: new FormControl<boolean>(false),
    org_form_routine: new FormControl<boolean>(false),
    org_form_project: new FormControl<boolean>(false),
    family_cooperation: new FormControl<string>(''),
    socioemotional_support_needed: new FormControl<boolean>(false),
    socioemotional_notes: new FormControl<string>(''),
    language_support_needed: new FormControl<boolean>(false),
    language_notes: new FormControl<string>(''),
    gross_motor_support_needed: new FormControl<boolean>(false),
    gross_motor_level: new FormControl<string>(''),
    gross_motor_diagnostic_date: new FormControl<string>(''),
    gross_motor_notes: new FormControl<string>(''),
    fine_motor_support_needed: new FormControl<boolean>(false),
    fine_motor_level: new FormControl<string>(''),
    fine_motor_diagnostic_date: new FormControl<string>(''),
    fine_motor_notes: new FormControl<string>(''),
    visual_perception_support_needed: new FormControl<boolean>(false),
    visual_perception_level: new FormControl<string>(''),
    visual_perception_diagnostic_date: new FormControl<string>(''),
    visual_perception_notes: new FormControl<string>(''),
    auditory_perception_support_needed: new FormControl<boolean>(false),
    auditory_perception_level: new FormControl<string>(''),
    auditory_perception_diagnostic_date: new FormControl<string>(''),
    auditory_perception_notes: new FormControl<string>(''),
    support_measures_recommended: new FormControl<boolean>(false),
    created: new FormControl<string>(''),
    modified: new FormControl<string>(''),
  });

  protected readonly compLearningSection: ChooseSection[] = [
    // 1. For edu
    {
      title: this.translocoService.translate('transition/report/sections/compLearning/forEducation'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/forEducation/describes'),
          formControlName: 'comp_learning_describes_success',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/forEducation/seeks'),
          formControlName: 'comp_learning_seeks_help',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/forEducation/persists'),
          formControlName: 'comp_learning_persists',
        },
      ],
    },
    // 2. Communication
    {
      title: this.translocoService.translate('transition/report/sections/compLearning/communication'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/communication/express'),
          formControlName: 'comp_communication_expresses_clearly',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/communication/listens'),
          formControlName: 'comp_communication_listens',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/communication/language'),
          formControlName: 'comp_communication_uses_language',
        },
      ],
    },
    // 3. Social
    {
      title: this.translocoService.translate('transition/report/sections/compLearning/social'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/social/strengths'),
          formControlName: 'comp_social_knows_strengths',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/social/cooperates'),
          formControlName: 'comp_social_cooperates',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/social/conflicts'),
          formControlName: 'comp_social_resolves_conflicts',
        },
      ],
    },
    // 4. Citizenship
    {
      title: this.translocoService.translate('transition/report/sections/compLearning/citizenship'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/citizenship/reflects'),
          formControlName: 'comp_citizenship_reflects',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/citizenship/creates'),
          formControlName: 'comp_citizenship_creates_rules',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/citizenship/empathy'),
          formControlName: 'comp_citizenship_empathy',
        },
      ],
    },
    // 5. Citizenship
    {
      title: this.translocoService.translate('transition/report/sections/compLearning/work'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/work/plans'),
          formControlName: 'comp_work_plans',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/work/completes'),
          formControlName: 'comp_work_completes',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/work/follow'),
          formControlName: 'comp_work_follows_rules',
        },
      ],
    },
    // 6. Solving
    {
      title: this.translocoService.translate('transition/report/sections/compLearning/solving'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/solving/proposes'),
          formControlName: 'comp_problem_solving_proposes',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/solving/describes'),
          formControlName: 'comp_problem_solving_describes',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/solving/seek'),
          formControlName: 'comp_problem_solving_seeks_alternatives',
        },
      ],
    },
    // 7. Cultural
    {
      title: this.translocoService.translate('transition/report/sections/compLearning/cultural'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/cultural/participates'),
          formControlName: 'comp_cultural_participates',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/cultural/understands'),
          formControlName: 'comp_cultural_understands_values',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/cultural/respects'),
          formControlName: 'comp_cultural_respects_diversity',
        },
      ],
    },
    // 8. Digital
    {
      title: this.translocoService.translate('transition/report/sections/compLearning/digital'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/digital/uses'),
          formControlName: 'comp_digital_uses_tools',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/digital/understands'),
          formControlName: 'comp_digital_understands_purpose',
        },
        {
          label: this.translocoService.translate('transition/report/sections/compLearning/digital/follow'),
          formControlName: 'comp_digital_follows_rules',
        },
      ],
    },
  ];

  protected readonly methodsSection: ChooseSection[] = [
    // 1. Edu method
    {
      title: this.translocoService.translate('transition/report/sections/methods/edu'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/speaking'),
          formControlName: 'method_speaking_listening',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/speaking/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/demonstration'),
          formControlName: 'method_demonstration',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/demonstration/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/manipulation'),
          formControlName: 'method_manipulation',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/manipulation/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/role'),
          formControlName: 'method_role_play',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/role/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/learning'),
          formControlName: 'method_learning_by_rules',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/learning/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/creative'),
          formControlName: 'method_creative_learning',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/creative/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/reflection'),
          formControlName: 'method_reflection',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/reflection/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/group'),
          formControlName: 'method_group_learning',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/group/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/edu/digital'),
          formControlName: 'method_digital_support',
          tooltip: this.translocoService.translate('transition/report/sections/methods/edu/digital/tooltip'),
        },
      ],
    },
    // 2. Org. form
    {
      title: this.translocoService.translate('transition/report/sections/methods/org'),
      controls: [
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/collective'),
          formControlName: 'org_form_collective',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/collective/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/group'),
          formControlName: 'org_form_group',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/group/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/activity'),
          formControlName: 'org_form_activity_centers',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/activity/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/individual'),
          formControlName: 'org_form_individual',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/individual/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/free'),
          formControlName: 'org_form_free_play',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/free/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/outdoor'),
          formControlName: 'org_form_outdoor',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/outdoor/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/circle'),
          formControlName: 'org_form_circle_time',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/circle/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/routine'),
          formControlName: 'org_form_routine',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/routine/tooltip'),
        },
        {
          label: this.translocoService.translate('transition/report/sections/methods/org/project'),
          formControlName: 'org_form_project',
          tooltip: this.translocoService.translate('transition/report/sections/methods/org/project/tooltip'),
        },
      ],
    },
  ];

  protected readonly supportSection: SupportSection[] = [
    { key: 'socioemotional', hasLevel: false },
    { key: 'language', hasLevel: false },
    { key: 'gross_motor', hasLevel: true },
    { key: 'fine_motor', hasLevel: true },
    { key: 'visual_perception', hasLevel: true },
    { key: 'auditory_perception', hasLevel: true },
  ];

  protected readonly competenceLevels: { title: string; value: CompetenceLevel }[] = [
    {
      title: this.translocoService.translate('transition/report/competenceLevel/low'),
      value: 'low',
    },
    {
      title: this.translocoService.translate('transition/report/competenceLevel/reduced'),
      value: 'reduced',
    },
    {
      title: this.translocoService.translate('transition/report/competenceLevel/adequate'),
      value: 'adequate',
    },
    {
      title: this.translocoService.translate('transition/report/competenceLevel/very_good'),
      value: 'very_good',
    },
  ];

  protected readonly loading$ = new BehaviorSubject(true);

  ngOnInit(): void {
    this.childUuid$
      .pipe(
        tap(() => {
          this.spinner.show();
          this.loading$.next(true);
        }),
        switchMap((childUuid) =>
          this.transitionReportService.getReport(childUuid).pipe(
            catchError(() => {
              this.toastService.error(this.translocoService.translate('transition/report/loadingError'));
              return of(undefined);
            }),
            finalize(() => {
              this.spinner.hide();
            })
          )
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((reportData) => {
        this.loading$.next(false);
        this.transitionReportForm.patchValue({
          ...reportData,
          birth_date: this.ngbParserService.dateStringToNgbDate(reportData.birth_date),
        });
        // disable inputs
        this.supportSection.forEach((section) => {
          if (reportData[section.key + '_diagnostic_date']) {
            const switchControl = this.transitionReportForm.controls[section.key + '_support_needed'] as FormControl;
            const selectControl = this.transitionReportForm.controls[section.key + '_level'] as FormControl;
            switchControl?.disable();
            selectControl?.disable();
          }
        });
        this.changeDetectionRef.markForCheck();
      });
  }

  onSubmit() {
    const data = {
      ...this.transitionReportForm.getRawValue(),
      birth_date: this.ngbParserService.ngbDateToDateString(this.transitionReportForm.controls.birth_date.value),
    };

    delete data['district'];
    delete data['country'];

    this.spinner.show();

    this.childUuid$.pipe(switchMap((childUuid) => this.transitionReportService.updateReport(childUuid, data))).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastService.success(this.translocoService.translate('transition/report/saveSuccess'));
        this.goBack();
      },
      error: () => {
        this.spinner.hide();
        this.toastService.error(this.translocoService.translate('transition/report/saveError'));
      },
    });
  }

  goBack() {
    this.isExternalRedirect$.pipe(take(1)).subscribe((externalRedirect) => {
      if (externalRedirect) {
        // window.close();
        // window.history.back();
        history.go(-2);
        return;
      }
      this.router.navigate(this.links.childList());
    });
  }

  trackById(index: number) {
    return index;
  }
}
